import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../ui/card";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Label } from "../ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs";
import { Switch } from "../ui/switch";
import { ArrowLeft, Eye, Save, Zap, Settings, Palette, Type, Image, Code } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const TemplateGenerator = () => {
  const { user, token: authToken } = useAuth();
  const [searchParams] = useSearchParams();
  
  // State management
  const [template, setTemplate] = useState(null);
  const [placeholderValues, setPlaceholderValues] = useState({});
  const [customization, setCustomization] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [previewHtml, setPreviewHtml] = useState('');
  
  // Settings
  const [autoSave, setAutoSave] = useState(true);
  const [previewMode, setPreviewMode] = useState('desktop'); // desktop, tablet, mobile
  
  const templateId = searchParams.get('template');

  useEffect(() => {
    if (templateId) {
      fetchTemplate();
    } else {
      window.location.href = '/member/lead-capture/marketplace';
    }
  }, [templateId]);

  useEffect(() => {
    if (autoSave && template && Object.keys(placeholderValues).length > 0) {
      const timer = setTimeout(() => {
        handleSave();
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [placeholderValues, autoSave]);

  const fetchTemplate = async () => {
    try {
      setLoading(true);
      const response = await fetch(
        `${BACKEND_URL}/api/lead-capture/templates/${templateId}`,
        {
          headers: {
            'Authorization': `Bearer ${authToken}`
          }
        }
      );
      const data = await response.json();
      
      if (data.ok) {
        setTemplate(data.template);
        
        // Initialize placeholder values with defaults
        const initialValues = {};
        data.template.placeholders?.forEach(placeholder => {
          initialValues[placeholder.name] = placeholder.default_value || '';
        });
        setPlaceholderValues(initialValues);
        
        // Generate initial preview
        await generatePreview(initialValues, data.template);
      } else {
        toast.error("Template not found or access denied.");
        window.location.href = '/member/lead-capture/marketplace';
      }
    } catch (error) {
      console.error('Error fetching template:', error);
      toast.error("Failed to load template. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const generatePreview = async (values = placeholderValues, templateData = template) => {
    if (!templateData) return;
    
    try {
      setGenerating(true);
      const response = await fetch(
        `${BACKEND_URL}/api/lead-capture/generate`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${authToken}`
          },
          body: JSON.stringify({
            template_id: templateId,
            customizations: values
          })
        }
      );
      const data = await response.json();
      
      if (data.ok) {
        setCustomization(data.customization);
        setPreviewHtml(data.generated_html);
      }
    } catch (error) {
      console.error('Error generating preview:', error);
    } finally {
      setGenerating(false);
    }
  };

  const handlePlaceholderChange = (placeholderName, value) => {
    const newValues = {
      ...placeholderValues,
      [placeholderName]: value
    };
    setPlaceholderValues(newValues);
    
    // Generate preview with debouncing
    const timer = setTimeout(() => {
      generatePreview(newValues);
    }, 500);
    
    return () => clearTimeout(timer);
  };

  const handleSave = async () => {
    if (!customization) return;
    
    try {
      setSaving(true);
      const response = await fetch(
        `${BACKEND_URL}/api/member/lead-capture/customizations`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${authToken}`
          },
          body: JSON.stringify({
            customization_id: customization.id,
            customizations: placeholderValues
          })
        }
      );
      const data = await response.json();
      
      if (data.ok) {
        toast.success("Your customizations have been saved.");
      }
    } catch (error) {
      console.error('Error saving:', error);
      toast.error("Failed to save customizations.");
    } finally {
      setSaving(false);
    }
  };

  const handlePublish = async () => {
    if (!customization) return;
    
    try {
      const response = await fetch(
        `${BACKEND_URL}/api/lead-capture/publish/${customization.id}`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${authToken}`
          }
        }
      );
      const data = await response.json();
      
      if (data.ok) {
        toast.success(`Your lead capture page is now live at: ${data.public_url}`);
        
        // Redirect to lead management
        setTimeout(() => {
          window.location.href = '/member/lead-capture/leads';
        }, 3000);
      }
    } catch (error) {
      console.error('Error publishing:', error);
      toast.error("Failed to publish page. Please try again.");
    }
  };

  const groupedPlaceholders = template?.placeholders?.reduce((groups, placeholder) => {
    const category = placeholder.category || 'general';
    if (!groups[category]) groups[category] = [];
    groups[category].push(placeholder);
    return groups;
  }, {}) || {};

  const getPreviewClass = () => {
    const classes = {
      desktop: 'w-full',
      tablet: 'w-3/4 mx-auto',
      mobile: 'w-96 mx-auto'
    };
    return classes[previewMode] || 'w-full';
  };

  if (loading) {
    return (
      <div className="container mx-auto p-6">
        <div className="animate-pulse space-y-6">
          <div className="h-8 bg-gray-300 rounded w-1/4"></div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="space-y-4">
              <div className="h-64 bg-gray-300 rounded"></div>
              <div className="h-32 bg-gray-300 rounded"></div>
            </div>
            <div className="h-96 bg-gray-300 rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  if (!template) {
    return (
      <div className="container mx-auto p-6 text-center">
        <h2 className="text-2xl font-bold text-gray-900 mb-4">Template Not Found</h2>
        <p className="text-gray-600 mb-6">The requested template could not be loaded.</p>
        <Button onClick={() => window.location.href = '/member/lead-capture/marketplace'}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Marketplace
        </Button>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-4">
          <Button 
            variant="outline" 
            onClick={() => window.location.href = '/member/lead-capture/marketplace'}
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Marketplace
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{template.title}</h1>
            <p className="text-gray-600">{template.description}</p>
          </div>
        </div>
        
        <div className="flex items-center space-x-2">
          <Button variant="outline" onClick={handleSave} disabled={saving}>
            <Save className="h-4 w-4 mr-2" />
            {saving ? 'Saving...' : 'Save Draft'}
          </Button>
          <Button onClick={handlePublish} disabled={!customization}>
            <Zap className="h-4 w-4 mr-2" />
            Publish Page
          </Button>
        </div>
      </div>

      {/* Main Content */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Customization Panel */}
        <div className="space-y-6">
          {/* Settings */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <Settings className="h-5 w-5" />
                <span>Settings</span>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <Label htmlFor="auto-save">Auto-save changes</Label>
                <Switch
                  id="auto-save"
                  checked={autoSave}
                  onCheckedChange={setAutoSave}
                />
              </div>
              
              <div>
                <Label>Preview Mode</Label>
                <div className="flex space-x-2 mt-2">
                  {['desktop', 'tablet', 'mobile'].map((mode) => (
                    <Button
                      key={mode}
                      size="sm"
                      variant={previewMode === mode ? 'default' : 'outline'}
                      onClick={() => setPreviewMode(mode)}
                    >
                      {mode.charAt(0).toUpperCase() + mode.slice(1)}
                    </Button>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Template Customization */}
          <Card>
            <CardHeader>
              <CardTitle>Customize Your Page</CardTitle>
              <CardDescription>
                Fill in the fields below to personalize your lead capture page
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Tabs defaultValue="content">
                <TabsList className="grid grid-cols-4 w-full">
                  <TabsTrigger value="content" className="flex items-center space-x-1">
                    <Type className="h-4 w-4" />
                    <span>Content</span>
                  </TabsTrigger>
                  <TabsTrigger value="design" className="flex items-center space-x-1">
                    <Palette className="h-4 w-4" />
                    <span>Design</span>
                  </TabsTrigger>
                  <TabsTrigger value="media" className="flex items-center space-x-1">
                    <Image className="h-4 w-4" />
                    <span>Media</span>
                  </TabsTrigger>
                  <TabsTrigger value="advanced" className="flex items-center space-x-1">
                    <Code className="h-4 w-4" />
                    <span>Advanced</span>
                  </TabsTrigger>
                </TabsList>

                <div className="mt-6">
                  {Object.entries(groupedPlaceholders).map(([category, placeholders]) => (
                    <TabsContent key={category} value={category === 'hero' ? 'content' : category}>
                      <div className="space-y-4">
                        <h3 className="font-semibold text-lg capitalize">{category} Settings</h3>
                        {placeholders.map((placeholder) => (
                          <div key={placeholder.id} className="space-y-2">
                            <Label htmlFor={placeholder.name}>
                              {placeholder.label}
                              {placeholder.required && <span className="text-red-500 ml-1">*</span>}
                            </Label>
                            
                            {placeholder.type === 'textarea' ? (
                              <Textarea
                                id={placeholder.name}
                                value={placeholderValues[placeholder.name] || ''}
                                onChange={(e) => handlePlaceholderChange(placeholder.name, e.target.value)}
                                placeholder={placeholder.default_value}
                                maxLength={placeholder.max_length}
                              />
                            ) : (
                              <Input
                                id={placeholder.name}
                                type={placeholder.type === 'email' ? 'email' : 'text'}
                                value={placeholderValues[placeholder.name] || ''}
                                onChange={(e) => handlePlaceholderChange(placeholder.name, e.target.value)}
                                placeholder={placeholder.default_value}
                                maxLength={placeholder.max_length}
                              />
                            )}
                            
                            {placeholder.max_length && (
                              <div className="text-xs text-gray-500 text-right">
                                {(placeholderValues[placeholder.name] || '').length} / {placeholder.max_length}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </TabsContent>
                  ))}
                  
                  {/* Default Content Tab */}
                  <TabsContent value="content">
                    <div className="space-y-4">
                      <h3 className="font-semibold text-lg">Content Settings</h3>
                      {(groupedPlaceholders.hero || []).concat(
                        groupedPlaceholders.form || [],
                        groupedPlaceholders.benefits || [],
                        groupedPlaceholders.legal || []
                      ).map((placeholder) => (
                        <div key={placeholder.id} className="space-y-2">
                          <Label htmlFor={placeholder.name}>
                            {placeholder.label}
                            {placeholder.required && <span className="text-red-500 ml-1">*</span>}
                          </Label>
                          
                          {placeholder.type === 'textarea' ? (
                            <Textarea
                              id={placeholder.name}
                              value={placeholderValues[placeholder.name] || ''}
                              onChange={(e) => handlePlaceholderChange(placeholder.name, e.target.value)}
                              placeholder={placeholder.default_value}
                              maxLength={placeholder.max_length}
                            />
                          ) : (
                            <Input
                              id={placeholder.name}
                              type={placeholder.type === 'email' ? 'email' : 'text'}
                              value={placeholderValues[placeholder.name] || ''}
                              onChange={(e) => handlePlaceholderChange(placeholder.name, e.target.value)}
                              placeholder={placeholder.default_value}
                              maxLength={placeholder.max_length}
                            />
                          )}
                          
                          {placeholder.max_length && (
                            <div className="text-xs text-gray-500 text-right">
                              {(placeholderValues[placeholder.name] || '').length} / {placeholder.max_length}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </TabsContent>

                  <TabsContent value="design">
                    <div className="text-center py-8 text-gray-500">
                      <Palette className="h-12 w-12 mx-auto mb-4" />
                      <p>Design customization coming soon!</p>
                      <p className="text-sm">Color themes, fonts, and layout options</p>
                    </div>
                  </TabsContent>

                  <TabsContent value="media">
                    <div className="text-center py-8 text-gray-500">
                      <Image className="h-12 w-12 mx-auto mb-4" />
                      <p>Media management coming soon!</p>
                      <p className="text-sm">Upload logos, images, and videos</p>
                    </div>
                  </TabsContent>

                  <TabsContent value="advanced">
                    <div className="text-center py-8 text-gray-500">
                      <Code className="h-12 w-12 mx-auto mb-4" />
                      <p>Advanced settings coming soon!</p>
                      <p className="text-sm">Custom CSS, tracking codes, and integrations</p>
                    </div>
                  </TabsContent>
                </div>
              </Tabs>
            </CardContent>
          </Card>
        </div>

        {/* Preview Panel */}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold">Live Preview</h3>
            {generating && (
              <div className="flex items-center space-x-2 text-blue-600">
                <div className="animate-spin h-4 w-4 border-2 border-blue-600 border-t-transparent rounded-full"></div>
                <span className="text-sm">Updating...</span>
              </div>
            )}
          </div>
          
          <Card className="min-h-96">
            <CardContent className="p-0">
              <div className={`transition-all duration-300 ${getPreviewClass()}`}>
                {previewHtml ? (
                  <iframe
                    srcDoc={previewHtml}
                    className="w-full h-96 border-0"
                    title="Template Preview"
                  />
                ) : (
                  <div className="flex items-center justify-center h-96 text-gray-500">
                    <div className="text-center">
                      <Eye className="h-12 w-12 mx-auto mb-4" />
                      <p>Preview will appear here</p>
                    </div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default TemplateGenerator;